import React, { useState } from 'react';
import Button from './ui/Button';
import { Menu, X, User, LogOut } from 'lucide-react';
import { useAuth } from '../lib/AuthContext';

const Navbar: React.FC = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const { user, logout: handleLogout } = useAuth();

  const scrollToSection = (id: string) => {
    setIsMobileMenuOpen(false);
    const currentPath = window.location.pathname;
    if (currentPath !== '/') {
      window.location.href = `/#${id}`;
    } else {
      const element = document.getElementById(id);
      if (element) {
        const yOffset = -80;
        const y = element.getBoundingClientRect().top + window.pageYOffset + yOffset;
        window.scrollTo({top: y, behavior: 'smooth'});
      }
    }
  };

  return (
    <nav className="sticky top-0 z-40 border-b border-white/10 bg-slate-900/80 backdrop-blur-md">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo Area */}
          <div className="flex items-center gap-2 cursor-pointer" onClick={() => window.scrollTo({top: 0, behavior: 'smooth'})}>
            <div className="h-6 w-6 bg-sky-500 rounded-sm shadow-lg shadow-sky-500/50"></div>
            <span className="text-white font-bold text-lg tracking-tight font-mono">ProGeoData</span>
          </div>
          
          {/* Desktop Navigation */}
          <div className="hidden md:block">
            <div className="ml-10 flex items-baseline space-x-8">
              <a href="/search" className="text-slate-300 hover:text-white transition-colors px-3 py-2 text-sm font-medium focus:outline-none focus:text-white">
                Search
              </a>
              <a href="/bulk-export" className="text-slate-300 hover:text-white transition-colors px-3 py-2 text-sm font-medium focus:outline-none focus:text-white">
                Bulk Export
              </a>
              <button onClick={() => scrollToSection('pricing')} className="text-slate-300 hover:text-white transition-colors px-3 py-2 text-sm font-medium focus:outline-none focus:text-white">
                Pricing
              </button>
              <a href="/chat" className="text-emerald-400 hover:text-emerald-300 transition-colors px-3 py-2 text-sm font-bold focus:outline-none">
                AI Chat
              </a>
              <a href="/api" className="text-amber-400 hover:text-amber-300 transition-colors px-3 py-2 text-sm font-bold focus:outline-none">
                x402 API
              </a>
              <a href="/docs" className="text-slate-300 hover:text-white transition-colors px-3 py-2 text-sm font-medium focus:outline-none focus:text-white">
                Docs
              </a>
              <button onClick={() => scrollToSection('mcp')} className="text-amber-400 hover:text-amber-300 font-bold px-3 py-2 text-sm focus:outline-none focus:text-white">
                AI Agents (MCP)
              </button>
            </div>
          </div>
          
          {/* Actions & Mobile Toggle */}
          <div className="flex items-center gap-4">
            <div className="hidden md:flex items-center gap-4">
              {user ? (
                <>
                  <a href="/dashboard" className="text-sm text-slate-400 hover:text-white">
                    <span className="text-sky-400 font-medium">{user.credits_remaining || 0}</span> credits
                  </a>
                  <a href="/dashboard" className="flex items-center gap-2 text-slate-300 hover:text-white">
                    <User className="w-4 h-4" />
                    <span className="text-sm">{user.email}</span>
                  </a>
                  <button
                    onClick={handleLogout}
                    className="text-slate-400 hover:text-white p-2"
                    title="Logout"
                  >
                    <LogOut className="w-4 h-4" />
                  </button>
                </>
              ) : (
                <>
                  <a href="/auth" className="text-slate-300 hover:text-white text-sm font-medium">
                    Sign In
                  </a>
                  <Button variant="primary" size="sm" onClick={() => window.location.href = '/auth?mode=register'}>
                    Get Started
                  </Button>
                </>
              )}
            </div>
            
            {/* Mobile Menu Button */}
            <div className="md:hidden">
              <button
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className="text-slate-300 hover:text-white p-2 focus:outline-none"
              >
                {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile Menu Dropdown */}
      {isMobileMenuOpen && (
        <div className="md:hidden bg-slate-900 border-b border-slate-800 animate-fade-in-up">
          <div className="px-4 pt-2 pb-4 space-y-2">
            <a
              href="/search"
              className="block w-full text-left px-3 py-2 rounded-md text-base font-medium text-slate-300 hover:text-white hover:bg-slate-800 transition-colors"
            >
              Search
            </a>
            <a
              href="/bulk-export"
              className="block w-full text-left px-3 py-2 rounded-md text-base font-medium text-slate-300 hover:text-white hover:bg-slate-800 transition-colors"
            >
              Bulk Export
            </a>
            <button
              onClick={() => scrollToSection('pricing')}
              className="block w-full text-left px-3 py-2 rounded-md text-base font-medium text-slate-300 hover:text-white hover:bg-slate-800 transition-colors"
            >
              Pricing
            </button>
            <a
              href="/chat"
              className="block w-full text-left px-3 py-2 rounded-md text-base font-bold text-emerald-400 hover:text-emerald-300 hover:bg-slate-800 transition-colors"
            >
              AI Chat
            </a>
            <a
              href="/api"
              className="block w-full text-left px-3 py-2 rounded-md text-base font-bold text-amber-400 hover:text-amber-300 hover:bg-slate-800 transition-colors"
            >
              x402 API
            </a>
            <a
              href="/docs"
              className="block w-full text-left px-3 py-2 rounded-md text-base font-medium text-slate-300 hover:text-white hover:bg-slate-800 transition-colors"
            >
              Docs
            </a>
            <button
              onClick={() => scrollToSection('mcp')}
              className="block w-full text-left px-3 py-2 rounded-md text-base font-bold text-amber-400 hover:text-amber-300 hover:bg-slate-800 transition-colors"
            >
              AI Agents (MCP)
            </button>
            <div className="pt-4 mt-2 border-t border-slate-800">
              {user ? (
                <div className="space-y-3">
                  <div className="text-sm text-slate-400 text-center">
                    <span className="text-sky-400 font-medium">{user.credits_remaining || 0}</span> credits remaining
                  </div>
                  <div className="text-sm text-slate-300 text-center">{user.email}</div>
                  <Button variant="outline" size="lg" className="w-full" onClick={handleLogout}>
                    Sign Out
                  </Button>
                </div>
              ) : (
                <div className="space-y-2">
                  <Button variant="primary" size="lg" className="w-full" onClick={() => window.location.href = '/auth?mode=register'}>
                    Get Started
                  </Button>
                  <a href="/auth" className="block text-center text-slate-400 hover:text-white text-sm py-2">
                    Already have an account? Sign In
                  </a>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;